import dayjs from 'dayjs';
import { useCallback, useMemo } from 'react';

import { MONTHS_RU } from '../data/constants';
import { buildCalendarGrid } from '../lib/buildCalendarGrid';

import type { Dayjs } from 'dayjs';

export type PeriodPickerDayModifier =
  | 'selected'
  | 'range-start'
  | 'range-end'
  | 'in-range';

interface UsePeriodPickerParams {
  viewMonth: Dayjs;
  startDate: Dayjs | null;
  endDate: Dayjs | null;
  isSelecting: boolean;
  previewStart: Dayjs | null;
  previewEnd: Dayjs | null;
  onViewMonthChange(month: Dayjs): void;
}

export const usePeriodPicker = ({
  viewMonth,
  startDate,
  endDate,
  isSelecting,
  previewStart,
  previewEnd,
  onViewMonthChange,
}: UsePeriodPickerParams) => {
  const grid = useMemo(() => buildCalendarGrid(viewMonth), [viewMonth]);

  // While the second bound is being picked, the hover preview is drawn
  // instead of the stored period.
  const isPreview = isSelecting && !!previewStart && !!previewEnd;
  const rangeStart = isPreview ? previewStart : startDate;
  const rangeEnd = isPreview ? previewEnd : endDate;

  const getDayModifier = useCallback(
    (day: Dayjs): PeriodPickerDayModifier | null => {
      if (!rangeStart || !rangeEnd) return null;

      if (rangeStart.isSame(rangeEnd, 'day')) {
        return day.isSame(rangeStart, 'day') ? 'selected' : null;
      }

      if (day.isSame(rangeStart, 'day')) return 'range-start';
      if (day.isSame(rangeEnd, 'day')) return 'range-end';
      if (day.isAfter(rangeStart, 'day') && day.isBefore(rangeEnd, 'day')) {
        return 'in-range';
      }

      return null;
    },
    [rangeStart, rangeEnd]
  );

  const isToday = useCallback(
    (day: Dayjs): boolean => day.isSame(dayjs(), 'day'),
    []
  );

  const prevMonthHandler = useCallback(
    () => onViewMonthChange(viewMonth.subtract(1, 'month')),
    [onViewMonthChange, viewMonth]
  );

  const nextMonthHandler = useCallback(
    () => onViewMonthChange(viewMonth.add(1, 'month')),
    [onViewMonthChange, viewMonth]
  );

  return {
    grid,
    monthLabel: `${MONTHS_RU[viewMonth.month()]} ${viewMonth.year()}`,
    getDayModifier,
    isToday,
    onPrevMonth: prevMonthHandler,
    onNextMonth: nextMonthHandler,
  };
};
